import styled, { css } from 'styled-components';

/**
 * 滚动条容器组件的 Props 接口
 */
interface ScrollableContainerProps {
  /** 最大高度 */
  maxHeight?: string | number;
  /** 最大宽度 */
  maxWidth?: string | number;
  /** 滚动条主题: 'light' 用于白色背景, 'dark' 用于黑色背景 */
  theme?: 'light' | 'dark';
  /** 是否显示水平滚动条 */
  overflowX?: 'auto' | 'scroll' | 'hidden' | 'visible';
  /** 是否显示垂直滚动条 */
  overflowY?: 'auto' | 'scroll' | 'hidden' | 'visible';
}

/**
 * 滚动条样式 Mixin - 浅色主题 (用于白色背景)
 */
const lightScrollbarStyles = css`
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }

  /* Firefox 滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
`;

/**
 * 滚动条样式 Mixin - 深色主题 (用于黑色/深色背景)
 */
const darkScrollbarStyles = css`
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  /* Firefox 滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
`;

/**
 * 可滚动容器组件
 *
 * @example
 * ```tsx
 * // 用于 Tooltip 等深色背景
 * <ScrollableContainer maxHeight={400} theme="dark">
 *   {content}
 * </ScrollableContainer>
 *
 * // 用于普通白色卡片背景
 * <ScrollableContainer maxHeight="500px" theme="light">
 *   {content}
 * </ScrollableContainer>
 * ```
 */
export const ScrollableContainer = styled.div<ScrollableContainerProps>`
  max-height: ${(props) =>
    typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight};
  max-width: ${(props) =>
    typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth};
  overflow-x: ${(props) => props.overflowX || 'hidden'};
  overflow-y: ${(props) => props.overflowY || 'auto'};

  /* 根据主题应用不同的滚动条样式 */
  ${(props) => (props.theme === 'dark' ? darkScrollbarStyles : lightScrollbarStyles)}
`;

// 设置默认 props
ScrollableContainer.defaultProps = {
  theme: 'light',
  overflowX: 'hidden',
  overflowY: 'auto',
};
